import type { KnowledgeMap, Placement, Topic, TopicSummary, Trail, Viewport } from "./types";

async function request<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(path, { signal, credentials: "same-origin" });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(payload.message ?? payload.detail?.message ?? "Request failed");
  }
  return response.json() as Promise<T>;
}

export function getMaps(signal?: AbortSignal): Promise<KnowledgeMap[]> {
  return request("/api/v1/maps", signal);
}

export function getMap(id: string, signal?: AbortSignal): Promise<KnowledgeMap> {
  return request(`/api/v1/maps/${id}`, signal);
}

export function getViewport(
  mapId: string,
  bounds: { min_x: number; min_y: number; max_x: number; max_y: number },
  zoom: number,
  signal?: AbortSignal,
): Promise<Viewport> {
  const query = new URLSearchParams({
    min_x: String(bounds.min_x),
    min_y: String(bounds.min_y),
    max_x: String(bounds.max_x),
    max_y: String(bounds.max_y),
    zoom: String(zoom),
  });
  return request(`/api/v1/maps/${mapId}/viewport?${query}`, signal);
}

export type RoadResponse = { coordinate_version: number; roads: { id: string; geometry: { points: [number, number][] }; style: string; version: number }[]; has_more: boolean };
export function getRoads(mapId: string, bounds: { min_x: number; min_y: number; max_x: number; max_y: number }, signal?: AbortSignal): Promise<RoadResponse> {
  const query = new URLSearchParams(Object.entries(bounds).map(([key,value]) => [key,String(value)]));
  return request(`/api/v1/maps/${mapId}/roads?${query}`, signal);
}
export type RoadTrails = { coordinate_version:number; road_id:string; style:string; trails:{id:string;title:string;summary:string;next_topic:{id:string;title:string;canonical_url:string};transition:string}[] };
export function getRoadTrails(roadId:string,signal?:AbortSignal):Promise<RoadTrails>{
  return request(`/api/v1/roads/${roadId}/trails`,signal);
}
export function getTrailRoads(trailId: string, signal?: AbortSignal): Promise<{ coordinate_version: number; segments: { road_id: string; trail_edge_id: string; sequence: number; direction: number; points: [number,number][] }[] }> {
  return request(`/api/v1/trails/${trailId}/roads`, signal);
}
export type GeneratedTerrain = { coordinate_version: number; generator_version: number; features: { id: string; feature_type: "polyline" | "polygon"; geometry: { points: [number,number][] }; style: { fill?: string; stroke?: string } }[]; has_more: boolean };
export function getTerrain(mapId: string, bounds: { min_x: number; min_y: number; max_x: number; max_y: number }, zoom: number, signal?: AbortSignal): Promise<GeneratedTerrain> {
  const query = new URLSearchParams([...Object.entries(bounds).map(([key,value]) => [key,String(value)]),["zoom",String(zoom)]]);
  return request(`/api/v1/maps/${mapId}/terrain?${query}`, signal);
}

export function getTopic(id: string, signal?: AbortSignal): Promise<Topic> {
  return request(`/api/v1/topics/${id}`, signal);
}

export type MapCategory = { id: string; title: string; parent_topic_id: string | null; depth: number; visible_from_zoom: number; child_count: number };

export function getCategories(mapId: string): Promise<MapCategory[]> {
  return request(`/api/v1/maps/${mapId}/categories`);
}

export function getTopicPlacements(topicId: string): Promise<Placement[]> {
  return request(`/api/v1/topics/${topicId}/placements`);
}
async function mutate<T>(path: string, method: string, csrfToken: string, data?: unknown): Promise<T> {
  const response = await fetch(path, {
    method, credentials: "same-origin",
    headers: { "X-CSRF-Token": csrfToken, ...(data === undefined ? {} : { "Content-Type": "application/json" }) },
    ...(data === undefined ? {} : { body: JSON.stringify(data) }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.detail?.message ?? (Array.isArray(payload.detail)?payload.detail[0]?.msg:undefined) ?? payload.message ?? `Request failed (${response.status})`);
  }
  return response.json() as Promise<T>;
}

export function movePlacement(item: Placement, x: number, y: number, csrfToken: string): Promise<Placement> {
  return mutate(`/api/v1/placements/${item.id}`, "PATCH", csrfToken, { base_version: item.version, x, y });
}
export function setPlacementLock(item: Placement, locked: boolean, csrfToken: string): Promise<Placement> {
  return mutate(`/api/v1/placements/${item.id}/lock`,"PATCH",csrfToken,{base_version:item.version,manually_locked:locked});
}

export function changeCategory(topicId: string, categoryTopicId: string | null, baseVersion: number, csrfToken: string): Promise<{ placement: Placement }> {
  return mutate(`/api/v1/topics/${topicId}/category`, "PATCH", csrfToken,
    { category_topic_id: categoryTopicId, base_version: baseVersion });
}

export function deleteTopic(topicId: string, csrfToken: string): Promise<{ status: string }> {
  return mutate(`/api/v1/topics/${topicId}`, "DELETE", csrfToken);
}

export function searchTopics(query: string, signal?: AbortSignal): Promise<{ items: TopicSummary[] }> {
  return request(`/api/v1/topics?q=${encodeURIComponent(query)}&limit=100`, signal);
}

export function getTrails(signal?: AbortSignal): Promise<{ id: string; title: string; summary: string }[]> {
  return request("/api/v1/trails", signal);
}

export function getTrail(id: string, signal?: AbortSignal): Promise<Trail> {
  return request(`/api/v1/trails/${id}`, signal);
}

export type Identity =
  | { authenticated: false }
  | { authenticated: true; id: string; display_name: string; role: string; csrf_token: string | null };

export function getIdentity(signal?: AbortSignal): Promise<Identity> {
  return request("/api/v1/auth/me", signal);
}

export async function logout(csrfToken: string): Promise<void> {
  const response = await fetch("/api/v1/auth/logout", {
    method: "POST",
    credentials: "same-origin",
    headers: { "X-CSRF-Token": csrfToken },
  });
  if (!response.ok) throw new Error("Sign out failed");
}

export type ManagedContent = {
  id: string; title: string; summary: string; status: string; review_state: string;
  created_at: string; updated_at: string; author: string; version: number;
  published_version: number | null; map_id: string | null; category_topic_id: string | null;
  level: number; on_map: boolean;
  content?: { schema_version: number; blocks: import("./types").ContentBlock[] };
};
export type ManagedContentPage = { items: ManagedContent[]; total: number; page: number; per_page: number };
export function listManagedContent(page: number, status: "all"|"unpublished"|"published"): Promise<ManagedContentPage> {
  return request(`/api/v1/admin/content?page=${page}&per_page=20&status=${status}`);
}
export function getManagedContent(id: string): Promise<ManagedContent> {
  return request(`/api/v1/admin/content/${id}`);
}
export type SemanticRelationship = {
  id:string;source_topic_id:string;target_topic_id:string;source_title:string;target_title:string;
  kind:"related_to"|"prerequisite_for"|"example_of"|"contradicts"|"broader_than";
  explanation:string;status:string;version:number;
};
export type RelationshipInput = Pick<SemanticRelationship,"source_topic_id"|"target_topic_id"|"kind"|"explanation">;
export function listAdminRelationships(topicId:string):Promise<SemanticRelationship[]>{
  return request(`/api/v1/admin/relationships?topic_id=${encodeURIComponent(topicId)}`);
}
export function createAdminRelationship(data:RelationshipInput,csrfToken:string):Promise<SemanticRelationship>{
  return mutate("/api/v1/admin/relationships","POST",csrfToken,data);
}
export function updateAdminRelationship(item:SemanticRelationship,data:RelationshipInput&{status?:"draft"|"published"},csrfToken:string):Promise<SemanticRelationship>{
  return mutate(`/api/v1/admin/relationships/${item.id}`,"PATCH",csrfToken,{...data,base_version:item.version});
}
export function archiveAdminRelationship(item:SemanticRelationship,csrfToken:string):Promise<{id:string;status:string;version:number}>{
  return mutate(`/api/v1/admin/relationships/${item.id}`,"DELETE",csrfToken,{base_version:item.version});
}
export type TrailEditorInput={
  title:string;summary:string;map_id:string|null;start_client_id:string;
  steps:{client_id:string;topic_id:string;preferred_placement_id:string|null;note:string}[];
  edges:{from_client_id:string;to_client_id:string;label:string;is_default:boolean}[];
};
export type AdminTrail={
  id:string;title:string;summary:string;map_id:string|null;status:string;version:number;start_step_id:string|null;
  steps:{id:string;topic_id:string;preferred_placement_id:string|null;note:string}[];
  edges:{id:string;from_step_id:string;to_step_id:string;label:string;is_default:boolean}[];
};
export type AdminTrailSummary=Pick<AdminTrail,"id"|"title"|"summary"|"map_id"|"status"|"version">;
export function listAdminTrails():Promise<AdminTrailSummary[]>{return request("/api/v1/admin/trails")}
export function getAdminTrail(id:string):Promise<AdminTrail>{return request(`/api/v1/admin/trails/${id}`)}
export function createAdminTrail(data:TrailEditorInput,csrfToken:string):Promise<{id:string}>{return mutate("/api/v1/trails","POST",csrfToken,data)}
export function updateAdminTrail(item:AdminTrail,data:TrailEditorInput,csrfToken:string):Promise<AdminTrail>{return mutate(`/api/v1/admin/trails/${item.id}`,"PATCH",csrfToken,{...data,base_version:item.version})}
export function archiveAdminTrail(item:AdminTrail,csrfToken:string):Promise<{id:string;status:string;version:number}>{return mutate(`/api/v1/admin/trails/${item.id}`,"DELETE",csrfToken,{base_version:item.version})}
export async function uploadContent(files: File[], csrfToken: string): Promise<{ids:string[];count:number}> {
  const form = new FormData();
  files.forEach(file=>form.append("files",file));
  const response = await fetch("/api/v1/admin/content/upload",{
    method:"POST",credentials:"same-origin",headers:{"X-CSRF-Token":csrfToken},body:form,
  });
  if(!response.ok){const payload=await response.json().catch(()=>({}));throw new Error(payload.detail?.message??"Upload failed")}
  return response.json();
}
export function saveManagedContent(id:string,data:{
  expected_revision:number;title:string;summary:string;
  content:{schema_version:number;blocks:import("./types").ContentBlock[]};
  map_id:string;category_topic_id:string|null;level:number;action:"save"|"publish";
},csrfToken:string):Promise<ManagedContent>{
  return mutate(`/api/v1/admin/content/${id}`,"PATCH",csrfToken,data);
}
export function publishManagedContent(id:string,expectedRevision:number,csrfToken:string):Promise<ManagedContent>{
  return mutate(`/api/v1/admin/content/${id}/publish`,"POST",csrfToken,{expected_revision:expectedRevision});
}
